/**
 * Walkthrough launcher + corner widget (ProjectTasks #109 Phase 1).
 *
 * Renders only in sandbox mode. Handles every state EXCEPT the actively-
 * running overlay (that's WalkthroughOverlay):
 *   - No active run, not yet dismissed → entry banner offering to start.
 *   - Active run, paused → a "Resume" pill with Restart / Exit.
 *   - Active run, not paused → renders nothing (the overlay is on screen).
 *
 * The entry-banner dismissal is remembered in localStorage so it is not
 * naggy across refreshes; "Start" from the banner clears nothing — a
 * completed run simply won't re-offer because completedScenarioIds tracks it.
 */

import { useState } from 'react'
import { Compass, Play, RotateCcw, X } from 'lucide-react'
import { useAppStore } from '@/store/appStore'
import { useWalkthroughStore } from '@/modules/walkthrough/walkthroughStore'
import { WALKTHROUGH_SCENARIOS } from '@/modules/walkthrough/scenarios'

// The sandbox launcher offers SIMULATIONS (build-a-dataset-by-hand) only.
// Cross-page journeys (kind 'journey') are the Help Center's to offer.
const SIMULATIONS = WALKTHROUGH_SCENARIOS.filter((s) => !s.kind || s.kind === 'simulation')

const BANNER_DISMISS_KEY = 'ppm-walkthrough-banner-dismissed'

function bannerDismissed(): boolean {
  try {
    return localStorage.getItem(BANNER_DISMISS_KEY) === '1'
  } catch {
    return false
  }
}

export function WalkthroughLauncher() {
  const appMode = useAppStore((s) => s.appMode)
  const activeScenarioId = useWalkthroughStore((s) => s.activeScenarioId)
  const paused = useWalkthroughStore((s) => s.paused)
  const start = useWalkthroughStore((s) => s.start)
  const resume = useWalkthroughStore((s) => s.resume)
  const restart = useWalkthroughStore((s) => s.restart)
  const exit = useWalkthroughStore((s) => s.exit)
  const activeScenario = useWalkthroughStore((s) => s.activeScenario)

  const [dismissed, setDismissed] = useState(bannerDismissed)

  // Only ever shown in sandbox mode.
  if (appMode !== 'sandbox') return null

  // Active + paused → resume pill.
  if (activeScenarioId && paused) {
    const scn = activeScenario()
    return (
      <div className="fixed bottom-4 right-4 z-[55] w-[280px] bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-2.5 bg-gradient-to-r from-brand-600 to-indigo-600 text-white flex items-center gap-2">
          <Compass className="w-4 h-4" />
          <span className="text-xs font-semibold">Walkthrough paused</span>
        </div>
        <div className="px-4 py-3">
          <p className="text-xs text-slate-600 mb-3">{scn?.name}</p>
          <div className="flex items-center gap-2">
            <button
              onClick={resume}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors"
            >
              <Play className="w-3.5 h-3.5" /> Resume
            </button>
            <button
              onClick={restart}
              className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-slate-600 rounded hover:bg-slate-100 transition-colors"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Restart
            </button>
            <button
              onClick={exit}
              className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-slate-500 rounded hover:bg-slate-100 transition-colors ml-auto"
            >
              Exit
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Active + not paused → overlay handles it; render nothing here.
  if (activeScenarioId) return null

  // No active run + dismissed → nothing.
  if (dismissed) return null

  // No active run + not dismissed → entry banner listing every scenario.
  if (SIMULATIONS.length === 0) return null

  return (
    <div className="fixed bottom-4 right-4 z-[55] w-[340px] bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden">
      <div className="px-4 py-2.5 bg-gradient-to-r from-brand-600 to-indigo-600 text-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Compass className="w-4 h-4" />
          <span className="text-xs font-semibold">Guided company simulation</span>
        </div>
        <button
          onClick={() => {
            try { localStorage.setItem(BANNER_DISMISS_KEY, '1') } catch { /* ignore */ }
            setDismissed(true)
          }}
          aria-label="Dismiss"
          className="p-1 rounded hover:bg-white/20 transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="px-4 py-3 space-y-3">
        <p className="text-[11px] text-slate-500 leading-relaxed">
          Build a realistic dataset by hand, step by step. Pick a starting scenario:
        </p>
        {SIMULATIONS.map((scenario) => (
          <div key={scenario.id} className="rounded-lg border border-slate-200 p-2.5">
            <div className="text-xs font-semibold text-slate-800">{scenario.name}</div>
            <p className="text-[11px] text-slate-500 leading-relaxed mt-0.5 mb-2">{scenario.description}</p>
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-slate-400">
                {scenario.steps.length} steps · ~{scenario.estMinutes} min
              </span>
              <button
                onClick={() => start(scenario.id)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors"
              >
                <Play className="w-3.5 h-3.5" /> Start the {scenario.name}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
